import { fetchApi } from "~/lib/api";
import type {
  ActivityLog,
  AdminGetUsersParams,
  AdminUser,
  AdminUserProfileUpdate,
  ContactMessage,
  Pagination,
  User,
  UserPayment,
  UserProfileUpdate,
} from "~/types";
import { serializeFilterParams } from "./utils";

/**
 * Customer users service - for authenticated customers
 */
export const customerUsersService = {
  /**
   * Get customer's own profile
   */
  async getProfile() {
    return fetchApi<User>("/customer/users/profile");
  },

  /**
   * Update customer's own profile
   */
  async updateProfile(data: Partial<UserProfileUpdate>) {
    return fetchApi<User>("/customer/users/profile", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete customer's own account (GDPR compliance)
   */
  async deleteAccount() {
    return fetchApi<{ message: string; id: string }>(
      "/customer/users/delete-account",
      {
        method: "POST",
      },
    );
  },

  async getMessages() {
    return fetchApi<ContactMessage[]>("/customer/users/messages");
  },
};

export const adminUsersService = {
  /**
   * Get all users with optional filters
   */
  async getUsers(params: AdminGetUsersParams = {}) {
    const query = serializeFilterParams(params);
    const endpoint = `/admin/users${query}`;
    return fetchApi<Pagination<AdminUser>>(endpoint);
  },

  /**
   * Get a single user by ID
   */
  async getUser(userId: string) {
    return fetchApi<AdminUser>(`/admin/users/${userId}`);
  },

  /**
   * Update a user
   */
  async updateUser(userId: string, data: Partial<AdminUserProfileUpdate>) {
    return fetchApi<AdminUser>(`/admin/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a user (soft delete)
   */
  async deleteUser(userId: string) {
    return fetchApi<{ message: string; id: string }>(`/admin/users/${userId}`, {
      method: "DELETE",
    });
  },

  /**
   * Restore a deleted user
   */
  async restoreUser(userId: string) {
    return fetchApi<{ message: string; user: AdminUser }>(
      `/admin/users/${userId}/restore`,
      {
        method: "POST",
      },
    );
  },

  /**
   * Permanently delete a user (hard delete)
   */
  async permanentDeleteUser(userId: string) {
    return fetchApi<{ message: string; id: string }>(
      `/admin/users/${userId}/permanent`,
      {
        method: "DELETE",
      },
    );
  },

  /**
   * Get payments for a specific user
   */
  async getUserPayments(userId: string) {
    return fetchApi<UserPayment[]>(`/admin/users/${userId}/payments`);
  },

  /**
   * Get activity logs for a specific user (orders' activity)
   */
  async getUserActivityLogs(userId: string) {
    return fetchApi<ActivityLog[]>(`/admin/users/${userId}/activity`);
  },
};
